import React, { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { SignatureCanvas } from "react-signature-canvas";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FileSignature, Plus, Trash2, Eraser, Printer, Mail } from "lucide-react";

/**
 * Bolla di consegna (Delivery Note) creation modal.
 * - Select seriali (already on the technician van) e/o materiali di consumo
 * - Firma digitale (canvas) opzionale
 * - Genera PDF e invia email al tecnico
 */
export default function BollaModal({ open, onOpenChange, techId, techName, techEmail, serials, materials, supplies, equipment, onCreated }) {
  const { t } = useTranslation();
  const [selectedSerials, setSelectedSerials] = useState(new Set());
  const [selectedEquipment, setSelectedEquipment] = useState(new Set());
  const [matRows, setMatRows] = useState([]); // [{key, material_id, name, unit, qty}]
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("digital"); // 'digital' | 'paper'
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Preseleziona tutti i seriali attualmente sul furgone
      setSelectedSerials(new Set((serials || []).map((s) => s.id)));
      // Preseleziona tutta l'attrezzatura assegnata al tecnico
      setSelectedEquipment(new Set((equipment || []).map((e) => e.id)));
      // Precarica righe materiali dalle consegne già registrate al tecnico.
      // material_id resta vuoto: la bolla è un documento di consegna storica,
      // non decrementa nuovamente lo stock (che era già stato aggiornato).
      const preRows = (supplies || []).map((s, i) => ({
        key: `sup-${s.id || i}`,
        material_id: "",
        name: s.material_name || "",
        unit: s.unit || "pz",
        qty: Number(s.quantity) || 1,
        from_supply: true,
      }));
      setMatRows(preRows);
      setNote("");
      setMode("digital");
      setSendEmail(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleSerial = (id) => {
    const next = new Set(selectedSerials);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedSerials(next);
  };

  const toggleEquipment = (id) => {
    const next = new Set(selectedEquipment);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedEquipment(next);
  };

  const selectAllSerials = () => {
    if (selectedSerials.size === serials.length) setSelectedSerials(new Set());
    else setSelectedSerials(new Set(serials.map((s) => s.id)));
  };

  const selectAllEquipment = () => {
    if (selectedEquipment.size === (equipment?.length || 0)) setSelectedEquipment(new Set());
    else setSelectedEquipment(new Set((equipment || []).map((e) => e.id)));
  };

  const addMatRow = () => {
    const first = materials[0];
    setMatRows((rows) => [
      ...rows,
      { key: `${Date.now()}-${Math.random()}`, material_id: first?.id || "", name: first?.name || "", unit: first?.unit || "pz", qty: 1 },
    ]);
  };
  const removeMatRow = (key) => setMatRows((rows) => rows.filter((r) => r.key !== key));
  const changeMatRow = (key, patch) => setMatRows((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const totalItems = selectedSerials.size + selectedEquipment.size + matRows.length;

  const clearSignature = () => sigRef.current?.clear();

  const downloadPdf = async (bid) => {
    try {
      const res = await api.get(`/bolle/${bid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bolla-${bid.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download PDF");
    }
  };

  const submit = async () => {
    if (!techId) return toast.error(t("auto.tecnico_non_selezionato"));
    if (totalItems === 0) return toast.error(t("auto.seleziona_almeno_un_articolo_o_un_materiale"));
    if (mode === "digital" && sigRef.current?.isEmpty()) {
      return toast.error("Firma vuota. Firma nel riquadro o scegli 'stampa cartacea'.");
    }
    setSubmitting(true);
    try {
      const items = [];
      // Seriali selezionati
      serials
        .filter((s) => selectedSerials.has(s.id))
        .forEach((s) => items.push({ type: "serial", ref: s.id, name: s.material_name || s.serial, qty: 1, unit: "pz", serial_number: s.serial }));
      // Attrezzatura selezionata
      (equipment || [])
        .filter((e) => selectedEquipment.has(e.id))
        .forEach((e) => items.push({
          type: "equipment", ref: e.id,
          name: e.name + ((e.brand || e.model) ? ` (${[e.brand, e.model].filter(Boolean).join(" ")})` : ""),
          qty: 1, unit: "pz", serial_number: e.serial || null,
        }));
      // Materiali di consumo
      matRows.forEach((r) => {
        if (r.qty > 0 && (r.material_id || r.name)) {
          items.push({ type: "material", ref: r.material_id || null, name: r.name, qty: Number(r.qty), unit: r.unit || "pz" });
        }
      });

      const { data: bolla } = await api.post("/bolle", { technician_id: techId, items, note });

      if (mode === "digital") {
        let dataUrl;
        try {
          dataUrl = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
        } catch (err) {
          // Fallback if trim-canvas fails (e.g. edge sig)
          dataUrl = sigRef.current.toDataURL("image/png");
        }
        await api.post(`/bolle/${bolla.id}/sign`, { signature_data_url: dataUrl, send_email: sendEmail });
        toast.success(sendEmail ? "Bolla firmata e inviata via email al tecnico" : "Bolla firmata");
      } else {
        toast.success(t("auto.bolla_creata_senza_firma_digitale"));
      }
      await downloadPdf(bolla.id);
      onOpenChange(false);
      onCreated && onCreated(bolla);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Errore imprevisto");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-[#0052FF]" /> Bolla di consegna a {techName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Seriali */}
          <div className="rounded-sm border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Seriali sul furgone ({serials.length})
              </div>
              {serials.length > 0 && (
                <button type="button" className="text-xs text-[#0052FF] hover:underline" onClick={selectAllSerials} data-testid="bolla-select-all-serials">
                  {selectedSerials.size === serials.length ? "Deseleziona tutti" : "Seleziona tutti"}
                </button>
              )}
            </div>
            {serials.length === 0 ? (
              <div className="text-xs italic text-slate-400">{t("auto.nessun_seriale_in_viaggio")}</div>
            ) : (
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {serials.map((s) => (
                  <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1 hover:bg-slate-50" data-testid={`bolla-serial-${s.serial}`}>
                    <Checkbox checked={selectedSerials.has(s.id)} onCheckedChange={() => toggleSerial(s.id)} />
                    <span className="font-mono text-sm font-semibold">{s.serial}</span>
                    <span className="text-xs text-slate-500">{s.material_name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Attrezzatura assegnata */}
          <div className="rounded-sm border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Attrezzatura assegnata ({(equipment || []).length})
              </div>
              {(equipment || []).length > 0 && (
                <button type="button" className="text-xs text-[#0052FF] hover:underline" onClick={selectAllEquipment} data-testid="bolla-select-all-equipment">
                  {selectedEquipment.size === (equipment || []).length ? "Deseleziona tutti" : "Seleziona tutti"}
                </button>
              )}
            </div>
            {(equipment || []).length === 0 ? (
              <div className="text-xs italic text-slate-400">{t("auto.nessuna_attrezzatura_assegnata_al_tecnico")}</div>
            ) : (
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {(equipment || []).map((e) => (
                  <label key={e.id} className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1 hover:bg-slate-50" data-testid={`bolla-equip-${e.id}`}>
                    <Checkbox checked={selectedEquipment.has(e.id)} onCheckedChange={() => toggleEquipment(e.id)} />
                    <span className="text-sm font-semibold text-slate-900">{e.name}</span>
                    <span className="text-xs text-slate-500">{[e.brand, e.model].filter(Boolean).join(" · ")}</span>
                    {e.serial && <span className="ml-auto font-mono text-[11px] text-slate-500">{e.serial}</span>}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Materiali di consumo */}
          <div className="rounded-sm border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.materiali_di_consumo")}</div>
              <Button size="sm" variant="outline" onClick={addMatRow} className="h-7 rounded-sm text-xs" data-testid="bolla-add-material">
                <Plus className="mr-1 h-3 w-3" /> Aggiungi riga
              </Button>
            </div>
            {matRows.length === 0 ? (
              <div className="text-xs italic text-slate-400">{t("auto.nessun_materiale_di_consumo_aggiunto")}</div>
            ) : (
              <div className="space-y-2">
                {matRows.map((r) => (
                  <div key={r.key} className="grid grid-cols-12 gap-2" data-testid={`bolla-mat-row-${r.key}`}>
                    <div className="col-span-7">
                      <Select
                        value={r.material_id || "__custom__"}
                        onValueChange={(v) => {
                          if (v === "__custom__") { changeMatRow(r.key, { material_id: "", name: "" }); return; }
                          const m = materials.find((x) => x.id === v);
                          if (m) changeMatRow(r.key, { material_id: m.id, name: m.name, unit: m.unit || "pz" });
                        }}
                      >
                        <SelectTrigger className="h-8 rounded-sm text-xs">
                          <SelectValue placeholder={t("auto.scegli_materiale")} />
                        </SelectTrigger>
                        <SelectContent>
                          {materials.map((m) => (
                            <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                          ))}
                          <SelectItem value="__custom__">— Personalizzato —</SelectItem>
                        </SelectContent>
                      </Select>
                      {!r.material_id && (
                        <Input
                          value={r.name}
                          onChange={(e) => changeMatRow(r.key, { name: e.target.value })}
                          placeholder="Descrizione libera"
                          className="mt-1 h-7 rounded-sm text-xs"
                        />
                      )}
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={r.qty}
                        onChange={(e) => changeMatRow(r.key, { qty: e.target.value })}
                        className="h-8 rounded-sm font-mono text-sm"
                        data-testid={`bolla-mat-qty-${r.key}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={r.unit}
                        onChange={(e) => changeMatRow(r.key, { unit: e.target.value })}
                        placeholder="pz"
                        className="h-8 rounded-sm text-xs"
                      />
                    </div>
                    <div className="col-span-1 flex items-center justify-end">
                      <Button size="sm" variant="ghost" onClick={() => removeMatRow(r.key)} className="h-8 w-8 p-0">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Note */}
          <div>
            <Label className="text-xs">Note (opzionale)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="rounded-sm text-sm" placeholder={t("auto.es_consegna_per_cantieri_della_settimana")} data-testid="bolla-note" />
          </div>

          {/* Firma */}
          <div className="rounded-sm border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.firma")}</div>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={() => setMode("digital")} className={`rounded-sm border px-2 py-1 ${mode === "digital" ? "border-[#0052FF] bg-blue-50 font-semibold text-[#0052FF]" : "border-slate-200 text-slate-600"}`} data-testid="bolla-mode-digital">
                  <FileSignature className="mr-1 inline h-3 w-3" /> Firma digitale
                </button>
                <button type="button" onClick={() => setMode("paper")} className={`rounded-sm border px-2 py-1 ${mode === "paper" ? "border-[#0052FF] bg-blue-50 font-semibold text-[#0052FF]" : "border-slate-200 text-slate-600"}`} data-testid="bolla-mode-paper">
                  <Printer className="mr-1 inline h-3 w-3" /> Stampa cartacea
                </button>
              </div>
            </div>

            {mode === "digital" ? (
              <div>
                <div className="rounded-sm border-2 border-dashed border-slate-300 bg-slate-50 p-1">
                  <SignatureCanvas
                    ref={sigRef}
                    penColor="#0f172a"
                    canvasProps={{
                      width: 640,
                      height: 180,
                      className: "w-full rounded-sm bg-white",
                      "data-testid": "bolla-signature-canvas",
                    }}
                  />
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <div className="text-[11px] text-slate-500">{t("auto.firma_nel_riquadro_con_dito_penna_o_mouse")}</div>
                  <button type="button" onClick={clearSignature} className="text-xs text-slate-500 hover:text-slate-900" data-testid="bolla-signature-clear">
                    <Eraser className="mr-1 inline h-3 w-3" /> Cancella
                  </button>
                </div>
                <label className="mt-2 flex items-center gap-2 text-xs">
                  <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(!!v)} data-testid="bolla-send-email" />
                  <Mail className="h-3 w-3" /> Invia bolla firmata via email a <b>{techEmail || "—"}</b>
                </label>
              </div>
            ) : (
              <div className="rounded-sm bg-amber-50 p-3 text-xs text-amber-800">
                La bolla sarà generata <b>{t("auto.senza_firma_digitale")}</b>. Verrà scaricata come PDF stampabile pronto per la firma cartacea del tecnico.
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <div className="mr-auto text-xs text-slate-500" data-testid="bolla-item-count">
            <b>{totalItems}</b> articoli in bolla
          </div>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={submitting || totalItems === 0} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="bolla-submit">
            {submitting ? "Creazione…" : (mode === "digital" ? "Firma e genera" : "Genera bolla")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
